import React from "react";
import { Row } from "@hex-insights/core";
import { EditMode, Form, SubmitButton } from "@hex-insights/forms";
import { useActivePageRegistration, useHistory } from "@hex-insights/router";
import { ButtonLink, UserDetailQuery, UserField, UserForm, useUserDetailQuery } from "@s-i-s-i/see-learning.shared";
import { AuthenticationContext } from "../../../Contexts";
import { OnboardingLayout } from "../Components";
import { onboardingDisplayNamePageInfo } from "../DisplayNamePage/pageinfo";
import { onboardingPrivacyStatementPageInfo } from "../PrivacyStatementPage/pageinfo";
import { onboardingProfilePageInfo } from "./pageinfo";

export function OnboardingProfilePage() {
	useActivePageRegistration(onboardingProfilePageInfo);

	const { user: authUser } = React.useContext(AuthenticationContext);
	const { data } = useUserDetailQuery({ variables: { id: authUser?.id ?? "0" }, skip: !authUser });
	const user = data?.user;

	if (!user) {
		return <OnboardingLayout page="profile">Loading...</OnboardingLayout>;
	}

	return <LoadedContent user={user} />;
}

const userProfileFields = {
	imageKey: true,
	imageContentType: true,
	givenName: true,
	familyName: true,
	nameOrder: true,
	email: true,
	description: true,
	dateOfBirth: true,
	gender: true,
	pronouns: true,
} as const;

type LoadedContentProps = {
	user: UserDetailQuery["user"];
};

function LoadedContent({ user }: LoadedContentProps) {
	const history = useHistory();
	const onSuccess = React.useCallback(() => {
		history.push(onboardingDisplayNamePageInfo.to);
	}, [history]);
	const { formState, onSubmit } = UserForm.useDetailForm({
		user,
		onSuccess,
		fields: userProfileFields,
		editMode: EditMode.WriteOnly,
	});

	return (
		<OnboardingLayout
			page="profile"
			footerContent={
				<Row justify="space-between">
					<ButtonLink variant="tertiary" to={onboardingPrivacyStatementPageInfo.to}>
						Back
					</ButtonLink>
					<SubmitButton onClick={formState.onSubmitWrapper(onSubmit)} submissionStatus={formState.submissionStatus}>
						Continue
					</SubmitButton>
				</Row>
			}
		>
			<Form
				formState={formState}
				name={`onboarding.profile.${user.id}`}
				onSubmit={formState.onSubmitWrapper(onSubmit)}
				noNotifications
				noNavigationPrompt
				noChangesDialog
				mergeUnsavedChangesAutomatically
			>
				<Form.Column style={{ margin: "0 auto" }}>
					<UserField.Image formState={formState} style={{ width: "100%" }} />
					<Row justify="spaced-start" overflow="wrap" withVerticalSpacing>
						<UserField.GivenName formState={formState} />
						<UserField.FamilyName formState={formState} />
					</Row>
					<UserField.NameOrder formState={formState} style={{ width: "20rem" }} />
					<UserField.Email formState={formState} instanceID={user.id} />
					<UserField.Description formState={formState} style={{ width: "100%" }} />
					<UserField.DateOfBirth formState={formState} />
					<UserField.Gender formState={formState} />
					<UserField.Pronouns formState={formState} />
				</Form.Column>
			</Form>
		</OnboardingLayout>
	);
}
